// File: /var/www/app2/frontend/src/components/OrganizationManagement.tsx

import React, { useState, useEffect } from "react";
import axios from "axios";
import { User } from "../App";

// Organization interface
interface Organization {
  id: number;
  name: string;
  description: string;
  active: boolean;
  user_count: number;
  active_users: number;
  supporter_count: number;
}

// Form data interface
interface FormData {
  name: string;
  description: string;
}

// Component props interface
interface OrganizationManagementProps {
  user: User;
}

function OrganizationManagement({ user }: OrganizationManagementProps): React.ReactElement {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [showAddForm, setShowAddForm] = useState<boolean>(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [showInactive, setShowInactive] = useState<boolean>(false);

  // Form data
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
  });

  // Filter organizations based on showInactive checkbox
  const filteredOrganizations: Organization[] = showInactive
    ? organizations
    : organizations.filter((org: Organization) => org.active);

  useEffect(() => {
    if (user.role === "admin" || user.role === "superadmin") {
      fetchOrganizations();
    }
  }, [user]);

  const fetchOrganizations = async (): Promise<void> => {
    try {
      const response = await axios.get("/api/organizations/manage");
      setOrganizations(response.data.organizations || []);
      setError("");
    } catch (error: any) {
      console.error("Failed to fetch organizations:", error);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const { name, value } = e.currentTarget;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const resetForm = (): void => {
    setFormData({
      name: "",
      description: "",
    });
    setEditingOrg(null);
    setShowAddForm(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.name.trim()) {
      setError("Organization name is required");
      return;
    }

    try {
      if (!editingOrg) {
        // Create new organization
        await axios.post("/api/organizations", {
          name: formData.name,
          description: formData.description,
        });
        setSuccess("Organization created successfully!");
      } else {
        // Update existing organization
        await axios.put(`/api/organizations/${editingOrg.id}`, {
          name: formData.name,
          description: formData.description,
          active: editingOrg.active,
        });
        setSuccess("Organization updated successfully!");
      }

      resetForm();
      fetchOrganizations();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to save organization");
    }
  };

  const handleEdit = (org: Organization): void => {
    setFormData({
      name: org.name,
      description: org.description || "",
    });
    setEditingOrg(org);
    setShowAddForm(true);
    setError("");
    setSuccess("");
  };

  const handleInactivate = async (
    orgId: number,
    orgName: string,
    userCount: number
  ): Promise<void> => {
    if (orgName === "MidIndia") {
      alert("Cannot inactivate MidIndia organization");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to inactivate "${orgName}"? This will remove the organization from ${userCount} user(s).`
      )
    ) {
      return;
    }

    try {
      await axios.delete(`/api/organizations/${orgId}`);
      setSuccess("Organization inactivated successfully");
      fetchOrganizations();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to inactivate organization");
    }
  };

  const handleActivate = async (org: Organization): Promise<void> => {
    if (
      !window.confirm(
        `Are you sure you want to reactivate "${org.name}"?`
      )
    ) {
      return;
    }

    try {
      await axios.put(`/api/organizations/${org.id}`, {
        name: org.name,
        description: org.description,
        active: true,
      });
      setSuccess("Organization reactivated successfully");
      fetchOrganizations();
    } catch (error: any) {
      setError(error.response?.data?.error || "Failed to reactivate organization");
    }
  };

  if (user.role !== "admin" && user.role !== "superadmin") {
    return (
      <div style={{ padding: "40px", color: "white", textAlign: "center" }}>
        <h2>Access Denied</h2>
        <p>Only administrators can manage organizations.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading organizations...</div>;
  }

  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "40px 20px" }}>
      <h2 style={{ color: "white", marginBottom: "30px" }}>
        Organization Management
      </h2>

      {/* Add/Edit Organization Form */}
      {showAddForm && (
        <div
          style={{
            background: "white",
            borderRadius: "8px",
            padding: "25px",
            marginBottom: "30px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          <h3 style={{ marginBottom: "20px", color: "#2d3748" }}>
            {editingOrg ? "Edit Organization" : "Add New Organization"}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={{ display: "grid", gap: "20px" }}>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Organization Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={editingOrg?.name === "MidIndia"}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor:
                      editingOrg?.name === "MidIndia" ? "#f7fafc" : "white",
                  }}
                />
              </div>

              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontWeight: "500",
                  }}
                >
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontFamily: "inherit",
                  }}
                />
              </div>
            </div>

            {error && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#fed7d7",
                  color: "#c53030",
                  borderRadius: "4px",
                }}
              >
                {error}
              </div>
            )}

            {success && (
              <div
                style={{
                  marginTop: "20px",
                  padding: "10px",
                  backgroundColor: "#c6f6d5",
                  color: "#276749",
                  borderRadius: "4px",
                }}
              >
                {success}
              </div>
            )}

            <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
              <button
                type="submit"
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#48bb78",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {editingOrg ? "Update Organization" : "Create Organization"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#718096",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Bar */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "20px",
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            console.log("Add Organization button clicked");
            setShowAddForm(true);
          }}
          style={{
            padding: "10px 20px",
            backgroundColor: "#4299e1",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.backgroundColor = "#3182ce")}
          onMouseOut={(e: React.MouseEvent<HTMLButtonElement>) => (e.currentTarget.style.backgroundColor = "#4299e1")}
        >
          + Add New Organization
        </button>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowInactive(e.target.checked)}
            style={{
              marginRight: "8px",
              cursor: "pointer",
              width: "16px",
              height: "16px",
            }}
          />
          Show Inactive Organizations
        </label>
      </div>

      {/* Organizations Table */}
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr
              style={{
                backgroundColor: "#f7fafc",
                borderBottom: "2px solid #e2e8f0",
              }}
            >
              <th style={{ padding: "12px", textAlign: "left" }}>Name</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Description</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Status</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Total Users</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Active Users
              </th>
              <th style={{ padding: "12px", textAlign: "left" }}>Supporters</th>
              <th style={{ padding: "12px", textAlign: "left" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrganizations.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    padding: "40px",
                    textAlign: "center",
                    color: "#718096",
                  }}
                >
                  No organizations found
                </td>
              </tr>
            ) : (
              filteredOrganizations.map((org: Organization) => (
                <tr key={org.id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "12px" }}>
                    <strong>{org.name}</strong>
                    {org.name === "MidIndia" && (
                      <span
                        style={{
                          marginLeft: "8px",
                          padding: "2px 6px",
                          backgroundColor: "#805ad5",
                          color: "white",
                          fontSize: "10px",
                          borderRadius: "3px",
                        }}
                      >
                        PROTECTED
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "12px", color: "#718096" }}>
                    {org.description || "-"}
                  </td>
                  <td style={{ padding: "12px" }}>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "12px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor: org.active ? "#c6f6d5" : "#fed7d7",
                        color: org.active ? "#22543d" : "#822727",
                      }}
                    >
                      {org.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>{org.user_count}</td>
                  <td style={{ padding: "12px" }}>{org.active_users}</td>
                  <td style={{ padding: "12px" }}>{org.supporter_count}</td>
                  <td style={{ padding: "12px" }}>
                    <button
                      type="button"
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        handleEdit(org);
                      }}
                      style={{
                        padding: "6px 12px",
                        backgroundColor: "#4299e1",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        marginRight: "5px",
                        fontSize: "14px",
                      }}
                    >
                      Edit
                    </button>
                    {org.name !== "MidIndia" && (
                      org.active ? (
                        <button
                          type="button"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            handleInactivate(org.id, org.name, org.user_count);
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#f56565",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Inactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.preventDefault();
                            handleActivate(org);
                          }}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#48bb78",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Activate
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div
        style={{
          marginTop: "20px",
          color: "white",
          fontSize: "14px",
          textAlign: "center",
        }}
      >
        Showing {filteredOrganizations.length} of {organizations.length} Organizations
        {!showInactive && organizations.some((org: Organization) => !org.active) && (
          <span style={{ marginLeft: "8px", opacity: 0.8 }}>
            ({organizations.filter((org: Organization) => !org.active).length} inactive hidden)
          </span>
        )}
      </div>
    </div>
  );
}

export default OrganizationManagement;
